import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import {
  FileText,
  ZoomIn,
  ZoomOut,
  Play,
  Square,
  Music,
  Scissors,
  Sparkles,
  LocateFixed,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  BookOpen,
  ScrollText,
} from 'lucide-react';
import { MidiNote, TimeSignature, DetectedChord } from '../types/midi';
import {
  renderPianoScore,
  ScoreTranscriptionOptions,
  MeasureBoundingBox,
} from '../core/scoreTranscriber';
import { getTicksPerMeasure } from '../core/timing';

interface PianoScoreViewProps {
  notes: MidiNote[];
  timeSignature: TimeSignature;
  ppq: number;
  bpm: number;
  currentTick: number;
  onSeek: (tick: number) => void;
  measureChords?: Map<number, DetectedChord[]>;
  isPlaying?: boolean;
  onPlay?: () => void;
  onStop?: () => void;
  autoScroll?: boolean;
  onToggleAutoScroll?: () => void;
  className?: string;
  isFullScreenExternal?: boolean;
  onToggleFullScreenExternal?: (val: boolean) => void;
}

const KEY_SIGNATURE_OPTIONS = [
  { id: 'C', label: 'C Major / A min (0 alt)' },
  { id: 'G', label: 'G Major (1♯)' },
  { id: 'D', label: 'D Major (2♯)' },
  { id: 'A', label: 'A Major (3♯)' },
  { id: 'E', label: 'E Major (4♯)' },
  { id: 'F', label: 'F Major (1♭)' },
  { id: 'Bb', label: 'Bb Major (2♭)' },
  { id: 'Eb', label: 'Eb Major (3♭)' },
  { id: 'Ab', label: 'Ab Major (4♭)' },
];

export const PianoScoreView: React.FC<PianoScoreViewProps> = ({
  notes,
  timeSignature,
  ppq,
  bpm,
  currentTick,
  onSeek,
  measureChords,
  isPlaying = false,
  onPlay,
  onStop,
  autoScroll = true,
  onToggleAutoScroll,
  className = '',
  isFullScreenExternal,
  onToggleFullScreenExternal,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [boundingBoxes, setBoundingBoxes] = useState<MeasureBoundingBox[]>([]);

  // Full Screen State
  const [internalFullScreen, setInternalFullScreen] = useState<boolean>(false);
  const isFullScreen = isFullScreenExternal !== undefined ? isFullScreenExternal : internalFullScreen;
  const setFullScreen = (val: boolean) => {
    if (onToggleFullScreenExternal) {
      onToggleFullScreenExternal(val);
    } else {
      setInternalFullScreen(val);
    }
  };

  // Pagination & Display Mode State
  const [viewMode, setViewMode] = useState<'paginated' | 'continuous'>('paginated');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [measuresPerPage] = useState<number>(4);
  const [totalScoreMeasures, setTotalScoreMeasures] = useState<number>(4);

  // Transcription options state
  const [keySignature, setKeySignature] = useState<string>('C');
  const [splitPitch, setSplitPitch] = useState<number>(60); // Middle C (C4)
  const [smartBass, setSmartBass] = useState<boolean>(true); // Auto-route lowest sustained note to bass stave
  const [polyphonyMode, setPolyphonyMode] = useState<'auto' | 'singleVoice'>('auto'); // Multi-voice preservation
  const [gridResolution, setGridResolution] = useState<'1/4' | '1/8' | '1/16'>('1/8');
  const [measuresPerRow, setMeasuresPerRow] = useState<number>(2);
  const [zoomLevel, setZoomLevel] = useState<number>(100); // 75% to 150%

  const ticksPerMeasure = useMemo(() => {
    return getTicksPerMeasure(timeSignature, ppq);
  }, [timeSignature, ppq]);

  const currentMeasureNumber = useMemo(() => {
    return Math.floor(currentTick / ticksPerMeasure) + 1;
  }, [currentTick, ticksPerMeasure]);

  const [resizeCounter, setResizeCounter] = useState<number>(0);

  // Total calculated pages
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalScoreMeasures / measuresPerPage));
  }, [totalScoreMeasures, measuresPerPage]);

  // Keep currentPage within bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Measure range for current page
  const pageStartMeasure = useMemo(() => {
    return (currentPage - 1) * measuresPerPage + 1;
  }, [currentPage, measuresPerPage]);

  const pageEndMeasure = useMemo(() => {
    return Math.min(totalScoreMeasures, (currentPage - 1) * measuresPerPage + measuresPerPage);
  }, [currentPage, measuresPerPage, totalScoreMeasures]);

  // Page Turn Handlers
  const goToPrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  }, [totalPages]);

  // Auto-adapt to container resizing (split view toggle, window resize, full-screen toggle)
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      setResizeCounter((prev) => prev + 1);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isFullScreen]);

  // Re-render score on SVG container
  useEffect(() => {
    if (!containerRef.current) return;

    const startMeasureIndex = viewMode === 'paginated' ? (currentPage - 1) * measuresPerPage : undefined;
    const maxMeasuresToRender = viewMode === 'paginated' ? measuresPerPage : undefined;

    const options: ScoreTranscriptionOptions = {
      splitPitch,
      smartBass,
      polyphonyMode,
      keySignature,
      gridResolution,
      measuresPerRow,
      scale: zoomLevel / 100,
      startMeasureIndex,
      maxMeasuresToRender,
    };

    try {
      const res = renderPianoScore(
        containerRef.current,
        notes,
        timeSignature,
        ppq,
        options,
        currentMeasureNumber,
        measureChords
      );
      setBoundingBoxes(res.boundingBoxes);
      if (res.totalMeasures) {
        setTotalScoreMeasures(res.totalMeasures);
      }
    } catch (err) {
      console.error('[PianoScoreView] Render error:', err);
    }
  }, [
    notes,
    timeSignature,
    ppq,
    splitPitch,
    smartBass,
    polyphonyMode,
    keySignature,
    gridResolution,
    measuresPerRow,
    zoomLevel,
    measureChords,
    resizeCounter,
    viewMode,
    currentPage,
    measuresPerPage,
    isFullScreen,
  ]);

  // Find bounding box of active measure for playback cursor overlay
  const activeMeasureBox = useMemo(() => {
    return (
      boundingBoxes.find((b) => currentTick >= b.startTick && currentTick < b.endTick) ||
      boundingBoxes.find((b) => b.measureIndex + 1 === currentMeasureNumber)
    );
  }, [boundingBoxes, currentTick, currentMeasureNumber]);

  // Playhead Cursor position across the active measure
  const playheadPosition = useMemo(() => {
    if (!activeMeasureBox) return null;
    const measureDuration = Math.max(1, activeMeasureBox.endTick - activeMeasureBox.startTick);
    const tickInMeasure = Math.max(0, Math.min(measureDuration, currentTick - activeMeasureBox.startTick));
    const progress = tickInMeasure / measureDuration;

    const startX = activeMeasureBox.noteStartX ?? activeMeasureBox.x;
    const endX = activeMeasureBox.noteEndX ?? (activeMeasureBox.x + activeMeasureBox.width);
    const noteAreaWidth = Math.max(20, endX - startX);
    const x = startX + progress * noteAreaWidth;

    return {
      x,
      y: activeMeasureBox.y,
      height: activeMeasureBox.height,
    };
  }, [activeMeasureBox, currentTick]);

  // Auto-page turn during playback
  useEffect(() => {
    if (!isPlaying || viewMode !== 'paginated') return;
    const pageForMeasure = Math.floor((currentMeasureNumber - 1) / measuresPerPage) + 1;
    if (pageForMeasure >= 1 && pageForMeasure <= totalPages && pageForMeasure !== currentPage) {
      setCurrentPage(pageForMeasure);
    }
  }, [isPlaying, currentMeasureNumber, viewMode, measuresPerPage, totalPages, currentPage]);

  // Auto-scroll in continuous mode
  const lastScoreScrollMeasureRef = useRef<number>(-1);
  useEffect(() => {
    if (!autoScroll || !isPlaying || !activeMeasureBox || viewMode === 'paginated') return;
    if (lastScoreScrollMeasureRef.current === activeMeasureBox.measureIndex) return;

    const measureElem = document.getElementById(`score-measure-box-${activeMeasureBox.measureIndex + 1}`);
    const scrollContainer = scrollContainerRef.current;
    if (measureElem && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elemRect = measureElem.getBoundingClientRect();

      if (elemRect.bottom > containerRect.bottom - 50 || elemRect.top < containerRect.top + 50) {
        lastScoreScrollMeasureRef.current = activeMeasureBox.measureIndex;
        measureElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [autoScroll, isPlaying, activeMeasureBox, viewMode]);

  useEffect(() => {
    if (!isPlaying) {
      lastScoreScrollMeasureRef.current = -1;
    }
  }, [isPlaying]);

  // Global Keyboard Shortcuts (Arrows for Page Turning, Esc for Full Screen, F for toggle)
  useEffect(() => {
    const handleScoreKeyDown = (e: KeyboardEvent) => {
      const activeTag = (document.activeElement?.tagName || '').toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') return;

      if (e.key === 'Escape' && isFullScreen) {
        e.preventDefault();
        setFullScreen(false);
      } else if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setFullScreen(!isFullScreen);
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        if (viewMode === 'paginated') {
          e.preventDefault();
          goToNextPage();
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        if (viewMode === 'paginated') {
          e.preventDefault();
          goToPrevPage();
        }
      } else if (e.key === 'Home' && isFullScreen) {
        e.preventDefault();
        setCurrentPage(1);
      } else if (e.key === 'End' && isFullScreen) {
        e.preventDefault();
        setCurrentPage(totalPages);
      }
    };

    window.addEventListener('keydown', handleScoreKeyDown);
    return () => window.removeEventListener('keydown', handleScoreKeyDown);
  }, [isFullScreen, viewMode, totalPages, goToNextPage, goToPrevPage]);

  // FULL SCREEN SCORE VIEW
  if (isFullScreen) {
    return (
      <div
        id="score-fullscreen-modal"
        className="fixed inset-0 z-50 bg-[#F4F1EA] flex flex-col select-none overflow-hidden animate-in fade-in duration-200"
      >
        {/* Minimal Performance Top Bar */}
        <header className="h-12 bg-white/95 backdrop-blur-xs border-b border-[#1B1B1B]/15 px-4 flex items-center justify-between shrink-0 shadow-xs z-30">
          {/* Left: Exit Full Screen & Piece Info */}
          <div className="flex items-center gap-3">
            <button
              id="score-exit-fullscreen-btn"
              type="button"
              onClick={() => setFullScreen(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1B1B1B] hover:bg-black text-white text-xs font-semibold rounded-xs shadow-xs transition-colors"
              title="Exit Full Screen Score (Esc)"
            >
              <Minimize2 className="w-3.5 h-3.5" />
              <span>Exit (Esc)</span>
            </button>

            <div className="hidden sm:flex items-center gap-2 text-xs text-[#1B1B1B]/80 font-medium">
              <span className="font-serif font-bold text-neutral-900">Piano Solo</span>
              <span>•</span>
              <span>Bar {currentMeasureNumber}</span>
              <span>•</span>
              <span>♩ = {bpm}</span>
              <span>•</span>
              <span>{timeSignature.beats}/{timeSignature.beatType}</span>
            </div>
          </div>

          {/* Center: Page Navigation (Tourner les pages) */}
          <div className="flex items-center gap-2 bg-[#F8F7F4] p-1 border border-[#1B1B1B]/20 rounded-xs shadow-xs">
            <button
              type="button"
              onClick={goToPrevPage}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-[#F2F0EB] disabled:opacity-30 disabled:hover:bg-white text-xs font-semibold text-[#1B1B1B] border border-[#1B1B1B]/20 transition-all rounded-xs"
              title="Previous Page (← or PageUp)"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden md:inline">Previous</span>
            </button>

            <div className="px-3 py-0.5 text-center">
              <div className="text-xs font-bold text-[#1B1B1B] font-mono">
                Page {currentPage} of {totalPages}
              </div>
              <div className="text-[10px] text-[#1B1B1B]/60 font-serif">
                Bars {pageStartMeasure}–{pageEndMeasure}
              </div>
            </div>

            <button
              type="button"
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 px-2.5 py-1 bg-[#1B1B1B] hover:bg-black disabled:opacity-30 disabled:hover:bg-[#1B1B1B] text-xs font-semibold text-white border border-[#1B1B1B] transition-all rounded-xs shadow-xs"
              title="Next Page (→ or PageDown)"
            >
              <span className="hidden md:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Right: Performance Controls (Play / Stop & Zoom) */}
          <div className="flex items-center gap-2">
            {onPlay && onStop && (
              <button
                type="button"
                onClick={isPlaying ? onStop : onPlay}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-all shadow-xs border ${
                  isPlaying
                    ? 'bg-amber-500 hover:bg-amber-600 text-black border-amber-600'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700'
                }`}
                title={isPlaying ? 'Pause Playback (Space)' : 'Start Playback (Space)'}
              >
                {isPlaying ? <Square className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current" />}
                <span className="hidden sm:inline">{isPlaying ? 'Stop' : 'Play'}</span>
              </button>
            )}

            {/* Zoom Controls */}
            <div className="flex items-center bg-white p-0.5 border border-[#1B1B1B]/20 text-xs">
              <button
                type="button"
                onClick={() => setZoomLevel((z) => Math.max(75, z - 10))}
                className="p-1 hover:bg-[#F2F0EB] text-[#1B1B1B]/70"
                title="Zoom Out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="px-1.5 text-[11px] font-mono text-[#1B1B1B]/70">{zoomLevel}%</span>
              <button
                type="button"
                onClick={() => setZoomLevel((z) => Math.min(150, z + 10))}
                className="p-1 hover:bg-[#F2F0EB] text-[#1B1B1B]/70"
                title="Zoom In"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </header>

        {/* Full-Screen Pure Sheet Music Stage Area */}
        <div className="flex-1 relative overflow-auto flex items-center justify-center p-4 sm:p-8 daw-scroll">
          {/* Left Clickable Page-Turn Hotspot (visible on hover) */}
          {currentPage > 1 && (
            <button
              type="button"
              onClick={goToPrevPage}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-40 p-3 bg-white/80 hover:bg-white text-neutral-800 border border-neutral-300 shadow-xl rounded-full transition-all opacity-40 hover:opacity-100 hover:scale-110"
              title="Previous Page (←)"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Right Clickable Page-Turn Hotspot (visible on hover) */}
          {currentPage < totalPages && (
            <button
              type="button"
              onClick={goToNextPage}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-40 p-3 bg-white/80 hover:bg-white text-neutral-800 border border-neutral-300 shadow-xl rounded-full transition-all opacity-40 hover:opacity-100 hover:scale-110"
              title="Next Page (→)"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Sheet Music Parchment Card */}
          <div
            id="score-sheet-paper-fullscreen"
            style={{
              zoom: zoomLevel !== 100 ? `${zoomLevel}%` : undefined,
            }}
            className="relative bg-white text-black shadow-2xl p-6 sm:p-10 w-full max-w-[1020px] min-w-[340px] border border-neutral-300 shrink-0 my-auto rounded-sm"
          >
            {/* Sheet Title */}
            <div className="text-center mb-6 pb-2 border-b border-neutral-200">
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900 tracking-wide">
                Piano Solo
              </h1>
              <div className="flex items-center justify-between text-xs text-neutral-600 font-serif italic mt-1 px-2">
                <span>
                  Tempo: ♩ = {bpm} • Time Signature: {timeSignature.beats}/{timeSignature.beatType}
                </span>
                <span>
                  Page {currentPage} of {totalPages} • Key: {keySignature}
                </span>
              </div>
            </div>

            {/* Clickable Overlay for Measures with Playhead Cursor */}
            <div className="relative">
              {playheadPosition && (
                <div
                  id="score-playback-cursor"
                  style={{
                    position: 'absolute',
                    left: `${playheadPosition.x}px`,
                    top: `${playheadPosition.y}px`,
                    height: `${playheadPosition.height}px`,
                    width: '2.5px',
                    transform: 'translateX(-50%)',
                  }}
                  className="pointer-events-none z-30"
                >
                  <div className="w-full h-full bg-amber-500 rounded-full shadow-[0_0_8px_rgba(245,158,11,0.9)] ring-1 ring-amber-400" />
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-amber-500 filter drop-shadow(0 1px 2px rgba(0,0,0,0.5))" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-400 shadow-sm" />
                </div>
              )}

              {boundingBoxes.map((box) => (
                <div
                  key={box.measureIndex}
                  id={`score-measure-box-${box.measureIndex + 1}`}
                  onClick={() => onSeek(box.startTick)}
                  style={{
                    position: 'absolute',
                    left: `${box.x}px`,
                    top: `${box.y}px`,
                    width: `${box.width}px`,
                    height: `${box.height}px`,
                  }}
                  className={`cursor-pointer rounded transition-all pointer-events-auto group ${
                    box.measureIndex + 1 === currentMeasureNumber
                      ? 'bg-amber-500/15 ring-2 ring-amber-500/50 shadow-sm'
                      : 'hover:bg-indigo-500/5'
                  }`}
                  title={`Click to seek to bar ${box.measureIndex + 1}`}
                >
                  <span className="opacity-0 group-hover:opacity-100 absolute -top-3 left-1 text-[9px] font-mono bg-neutral-900 text-amber-300 px-1 rounded shadow">
                    Bar {box.measureIndex + 1}
                  </span>
                </div>
              ))}

              {/* VexFlow Container */}
              <div ref={containerRef} className="score-svg-target flex justify-center" />
            </div>

            {/* Score Footer */}
            <div className="mt-8 pt-4 border-t border-neutral-200 flex items-center justify-between text-[11px] text-neutral-400 font-serif">
              <span>Chord Notator Performance Sheet</span>
              <span>
                Page {currentPage} of {totalPages} (Bars {pageStartMeasure}–{pageEndMeasure})
              </span>
            </div>
          </div>
        </div>

        {/* Minimal Bottom Performance Footer */}
        <footer className="h-8 bg-white/95 border-t border-[#1B1B1B]/15 px-4 flex items-center justify-between text-[11px] text-[#1B1B1B]/60 font-medium shrink-0">
          <div className="flex items-center gap-1.5">
            <span>Turn pages with</span>
            <kbd className="px-1.5 py-0.5 bg-neutral-100 border border-neutral-300 text-[10px] font-mono rounded">←</kbd>
            <kbd className="px-1.5 py-0.5 bg-neutral-100 border border-neutral-300 text-[10px] font-mono rounded">→</kbd>
            <span>or click side arrows •</span>
            <kbd className="px-1.5 py-0.5 bg-neutral-100 border border-neutral-300 text-[10px] font-mono rounded">Esc</kbd>
            <span>to exit</span>
          </div>

          {/* Quick Page Indicator Dots */}
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentPage(idx + 1)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  currentPage === idx + 1
                    ? 'bg-[#1B1B1B] scale-125'
                    : 'bg-neutral-300 hover:bg-neutral-500'
                }`}
                title={`Jump to Page ${idx + 1}`}
              />
            ))}
          </div>
        </footer>
      </div>
    );
  }

  // STANDARD / EMBEDDED PANE VIEW
  return (
    <div
      id="piano-score-view"
      className={`flex flex-col h-full bg-[#F8F7F4] border-t border-[#1B1B1B]/20 select-none ${className}`}
    >
      {/* Top Toolbar */}
      <div
        id="score-toolbar"
        className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[#F8F7F4] border-b border-[#1B1B1B]/20 text-xs text-[#1B1B1B] print:hidden"
      >
        {/* Left: Score Title & Main Stats */}
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1.5 font-bold text-[#1B1B1B]">
            <FileText className="w-4 h-4 text-[#2563eb]" />
            <span>Piano Score</span>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-[#1B1B1B]/70 font-mono bg-white px-2 py-0.5 border border-[#1B1B1B]/20">
            <span>{notes.length} notes</span>
            <span>•</span>
            <span>Bar {currentMeasureNumber}</span>
          </div>
        </div>

        {/* Center: Transcription Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Key Signature */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase font-bold text-[#1B1B1B]/60 font-mono">Key:</span>
            <select
              value={keySignature}
              onChange={(e) => setKeySignature(e.target.value)}
              className="bg-white border border-[#1B1B1B]/30 px-1.5 py-1 text-xs text-[#1B1B1B] focus:outline-none focus:border-[#2563eb]"
            >
              {KEY_SIGNATURE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id} className="bg-white text-[#1B1B1B]">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Split Pitch */}
          <div className="flex items-center gap-1">
            <Scissors className="w-3 h-3 text-[#1B1B1B]/50" />
            <span className="text-[10px] uppercase font-bold text-[#1B1B1B]/60 font-mono">Split:</span>
            <select
              value={splitPitch}
              onChange={(e) => setSplitPitch(Number(e.target.value))}
              className="bg-white border border-[#1B1B1B]/30 px-1.5 py-1 text-xs text-[#1B1B1B] focus:outline-none focus:border-[#2563eb]"
              title="Split point between Treble and Bass staves"
            >
              <option value={53}>F3 (53)</option>
              <option value={55}>G3 (55)</option>
              <option value={57}>A3 (57)</option>
              <option value={59}>B3 (59)</option>
              <option value={60}>C4 (60) → Treble</option>
              <option value={61}>C4 (61) → Bass (Left Hand)</option>
              <option value={62}>D4 (62)</option>
              <option value={64}>E4 (64) → Bass</option>
            </select>
          </div>

          {/* Smart Bass Toggle */}
          <button
            type="button"
            onClick={() => setSmartBass((b) => !b)}
            className={`px-2 py-1 text-xs border transition-colors flex items-center gap-1 ${
              smartBass
                ? 'bg-[#2563eb]/10 text-[#2563eb] border-[#2563eb]/40 font-semibold'
                : 'bg-white text-[#1B1B1B]/70 border-[#1B1B1B]/25 hover:text-[#1B1B1B]'
            }`}
            title="Smart Bass: automatically routes lowest arpeggio note to bass stave if empty"
          >
            <Sparkles className="w-3 h-3 text-[#2563eb]" />
            <span>Smart Bass</span>
          </button>

          {/* Polyphony Mode Toggle */}
          <button
            type="button"
            onClick={() => setPolyphonyMode((p) => (p === 'auto' ? 'singleVoice' : 'auto'))}
            className={`px-2 py-1 text-xs border transition-colors ${
              polyphonyMode === 'auto'
                ? 'bg-[#2563eb]/10 text-[#2563eb] border-[#2563eb]/40 font-semibold'
                : 'bg-white text-[#1B1B1B]/70 border-[#1B1B1B]/25 hover:text-[#1B1B1B]'
            }`}
            title="Polyphony: display sustained notes (whole/half) overlaid on arpeggios in Voice 2"
          >
            <span>{polyphonyMode === 'auto' ? 'Polyphony (2 Voices)' : '1 Voice'}</span>
          </button>

          {/* Grid Resolution */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase font-bold text-[#1B1B1B]/60 font-mono">Rhythm:</span>
            <div className="flex items-center bg-white p-0.5 border border-[#1B1B1B]/25">
              {(['1/4', '1/8', '1/16'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGridResolution(g)}
                  className={`px-1.5 py-0.5 text-[10px] font-mono transition-colors ${
                    gridResolution === g
                      ? 'bg-[#2563eb] text-white font-bold'
                      : 'text-[#1B1B1B]/60 hover:text-[#1B1B1B]'
                  }`}
                  title={g === '1/4' ? 'Quarter Notes' : g === '1/8' ? 'Eighth Notes' : 'Sixteenth Notes'}
                >
                  {g === '1/4' ? '1/4' : g === '1/8' ? '1/8' : '1/16'}
                </button>
              ))}
            </div>
          </div>

          {/* Measures Per Row */}
          <div className="hidden md:flex items-center gap-1">
            <span className="text-[10px] uppercase font-bold text-[#1B1B1B]/60 font-mono">System:</span>
            <div className="flex items-center bg-white p-0.5 border border-[#1B1B1B]/25">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setMeasuresPerRow(n)}
                  className={`px-1.5 py-0.5 text-[10px] transition-colors ${
                    measuresPerRow === n
                      ? 'bg-[#1B1B1B] text-white font-bold'
                      : 'text-[#1B1B1B]/60 hover:text-[#1B1B1B]'
                  }`}
                  title={`${n} bars per system`}
                >
                  {n} bars
                </button>
              ))}
            </div>
          </div>

          {/* Zoom */}
          <div
            id="score-view-zoom-group"
            className="flex items-center gap-0.5 bg-white p-0.5 border border-[#1B1B1B]/25"
            title="Score zoom level"
          >
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.max(75, z - 10))}
              className="p-1 text-[#1B1B1B]/60 hover:text-[#1B1B1B] hover:bg-[#F2F0EB] transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.min(150, z + 10))}
              className="p-1 text-[#1B1B1B]/60 hover:text-[#1B1B1B] hover:bg-[#F2F0EB] transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-3 h-3" />
            </button>
          </div>

          {/* Auto-Scroll Follow Playback Toggle */}
          {onToggleAutoScroll && (
            <button
              id="score-autoscroll-toggle-btn"
              type="button"
              onClick={onToggleAutoScroll}
              className={`flex items-center gap-1 px-2 py-1 text-xs transition-colors font-medium border ${
                autoScroll
                  ? 'bg-[#2563eb]/10 border-[#2563eb]/40 text-[#2563eb] shadow-xs'
                  : 'bg-white hover:bg-[#F2F0EB] border-[#1B1B1B]/25 text-[#1B1B1B]/70 hover:text-[#1B1B1B]'
              }`}
              title={`Auto-scroll follow playhead: ${autoScroll ? 'ENABLED' : 'DISABLED'}`}
            >
              <LocateFixed className={`w-3 h-3 ${autoScroll ? 'text-[#2563eb]' : 'text-[#1B1B1B]/50'}`} />
              <span className="hidden sm:inline">Follow</span>
            </button>
          )}
        </div>

        {/* Right: Page Turning Controls & Full Screen Button */}
        <div className="flex items-center gap-2">
          {/* View Mode Toggle (Pages vs Continuous) */}
          <div className="flex items-center bg-white p-0.5 border border-[#1B1B1B]/25">
            <button
              type="button"
              onClick={() => setViewMode('paginated')}
              className={`flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium transition-colors ${
                viewMode === 'paginated'
                  ? 'bg-[#1B1B1B] text-white'
                  : 'text-[#1B1B1B]/70 hover:text-[#1B1B1B]'
              }`}
              title="Page-by-page display with page turning"
            >
              <BookOpen className="w-3 h-3" />
              <span>Pages</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('continuous')}
              className={`flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium transition-colors ${
                viewMode === 'continuous'
                  ? 'bg-[#1B1B1B] text-white'
                  : 'text-[#1B1B1B]/70 hover:text-[#1B1B1B]'
              }`}
              title="Continuous vertical scroll mode"
            >
              <ScrollText className="w-3 h-3" />
              <span>Scroll</span>
            </button>
          </div>

          {/* Page Turning Buttons when in Paginated Mode */}
          {viewMode === 'paginated' && (
            <div className="flex items-center bg-white border border-[#1B1B1B]/25 p-0.5">
              <button
                type="button"
                onClick={goToPrevPage}
                disabled={currentPage <= 1}
                className="p-1 text-[#1B1B1B]/70 hover:text-[#1B1B1B] disabled:opacity-25 transition-colors"
                title="Previous Page (←)"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="px-1.5 text-xs font-mono font-semibold text-[#1B1B1B] select-none">
                {currentPage}/{totalPages}
              </span>
              <button
                type="button"
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
                className="p-1 text-[#1B1B1B]/70 hover:text-[#1B1B1B] disabled:opacity-25 transition-colors"
                title="Next Page (→)"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Dedicated Full Screen Button */}
          <button
            id="score-fullscreen-trigger-btn"
            type="button"
            onClick={() => setFullScreen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-[#1B1B1B] hover:bg-black text-white text-xs font-semibold shadow-xs transition-colors"
            title="Full Screen Sheet Music Performance Mode with Page Turning [Shortcut: F]"
          >
            <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
            <span>Full Screen</span>
          </button>
        </div>
      </div>

      {/* Main Score Canvas Area */}
      <div
        ref={scrollContainerRef}
        id="score-scroll-container"
        className="flex-1 overflow-auto p-3 sm:p-6 bg-[#F8F7F4] print:p-0 print:bg-white daw-scroll"
      >
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center p-8 max-w-md mx-auto my-auto">
            <div className="p-3 bg-white border border-[#1B1B1B]/20 text-[#2563eb] mb-3">
              <Music className="w-8 h-8 opacity-80" />
            </div>
            <h3 className="text-sm font-bold text-[#1B1B1B]">
              No notes to transcribe yet
            </h3>
            <p className="text-xs text-[#1B1B1B]/60 mt-1 leading-relaxed">
              Play on your MIDI keyboard or record notes from the transport bar.
              The piano sheet music will transcribe automatically in real-time!
            </p>
          </div>
        ) : (
          <div className="min-w-fit w-full flex justify-center pb-8">
            <div
              id="score-sheet-paper"
              style={{
                zoom: zoomLevel !== 100 ? `${zoomLevel}%` : undefined,
              }}
              className="relative bg-white text-black rounded-lg shadow-2xl p-4 sm:p-8 w-full max-w-[960px] min-w-[320px] border border-neutral-200 shrink-0 print:shadow-none print:border-none print:m-0 print:p-0"
            >
              {/* Sheet Music Header Title */}
              <div className="text-center mb-6 pb-2 border-b border-neutral-200">
                <h1 className="text-xl sm:text-2xl font-serif font-bold text-neutral-900 tracking-wide">
                  Piano Solo
                </h1>
                <div className="flex items-center justify-between text-xs text-neutral-600 font-serif italic mt-1 px-2">
                  <span>
                    Tempo: ♩ = {bpm} • Time Signature: {timeSignature.beats}/{timeSignature.beatType}
                  </span>
                  <span>
                    {viewMode === 'paginated' && `Page ${currentPage} of ${totalPages} • `}Key Signature: {keySignature}
                  </span>
                </div>
              </div>

              {/* Clickable Overlay for Measures with Playback Cursor */}
              <div className="relative">
                {/* Active Playback Cursor across Treble & Bass Staves */}
                {playheadPosition && (
                  <div
                    id="score-playback-cursor"
                    style={{
                      position: 'absolute',
                      left: `${playheadPosition.x}px`,
                      top: `${playheadPosition.y}px`,
                      height: `${playheadPosition.height}px`,
                      width: '2.5px',
                      transform: 'translateX(-50%)',
                    }}
                    className="pointer-events-none z-30"
                  >
                    <div className="w-full h-full bg-amber-500 rounded-full shadow-[0_0_8px_rgba(245,158,11,0.9)] ring-1 ring-amber-400" />
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-amber-500 filter drop-shadow(0 1px 2px rgba(0,0,0,0.5))" />
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-400 shadow-sm" />
                  </div>
                )}

                {boundingBoxes.map((box) => (
                  <div
                    key={box.measureIndex}
                    id={`score-measure-box-${box.measureIndex + 1}`}
                    onClick={() => onSeek(box.startTick)}
                    style={{
                      position: 'absolute',
                      left: `${box.x}px`,
                      top: `${box.y}px`,
                      width: `${box.width}px`,
                      height: `${box.height}px`,
                    }}
                    className={`cursor-pointer rounded transition-all pointer-events-auto group ${
                      box.measureIndex + 1 === currentMeasureNumber
                        ? 'bg-amber-500/15 ring-2 ring-amber-500/50 shadow-sm'
                        : 'hover:bg-indigo-500/5'
                    }`}
                    title={`Click to seek to bar ${box.measureIndex + 1}`}
                  >
                    <span className="opacity-0 group-hover:opacity-100 absolute -top-3 left-1 text-[9px] font-mono bg-neutral-900 text-amber-300 px-1 rounded shadow">
                      Bar {box.measureIndex + 1}
                    </span>
                  </div>
                ))}

                {/* VexFlow Container */}
                <div ref={containerRef} className="score-svg-target" />
              </div>

              {/* Score Footer */}
              <div className="mt-8 pt-4 border-t border-neutral-200 flex items-center justify-between text-[10px] text-neutral-400 font-serif">
                <span>Generated by Chord Notator Studio</span>
                <span>
                  {viewMode === 'paginated'
                    ? `Page ${currentPage} of ${totalPages} (Bars ${pageStartMeasure}–${pageEndMeasure})`
                    : 'Treble Clef (R.H.) • Bass Clef (L.H.)'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Stylesheet */}
      <style>{`
        @media print {
          body {
            background: white !important;
            color: black !important;
          }
          #score-toolbar,
          #transport-bar,
          #undo-btn,
          #redo-btn,
          header,
          nav {
            display: none !important;
          }
          #piano-score-view {
            border: none !important;
            height: auto !important;
            background: white !important;
          }
          #score-scroll-container {
            overflow: visible !important;
            padding: 0 !important;
            background: white !important;
          }
          #score-sheet-paper {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: none !important;
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
};
